import { Inject, Injectable, Optional, SkipSelf } from "@angular/core";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";

import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

import { ActivePossibilities } from '../../../../shared/suggestable-input/entities/active-possibilities.entity';
import { ActivityAwareDataSubService } from '../../../../shared/basic-data-management/activity-aware-data-subservice.class';
import { CachedDataSurveyService } from '../../../../technical-modules/tasks/services/cached-data-survey.service';
import { Suggestions } from '../../../../shared/suggestable-input/entities/suggestions.entity';

import { IntervenantEntity } from '../entities/intervenant.entity';
import { IntervenantsFilters } from '../entities/intervenants-filters.entity';
import { IntervenantTLR } from '../entities/intervenant-tlr.entity';
import { PdpTypeSigned } from '../entities/pdp-type-signed';
import { SuggestionNomIntervenant } from '../entities/suggestion-nom-intervenant.entity';

@Injectable({ providedIn: 'root' })
export class IntervenantService {
	private path: string;
	
	private intervenantTLRSubService!: ActivityAwareDataSubService<IntervenantTLR>;

	private notifyChanges = () => {
		// Indique immediatement un changement sur les donnees aux caches sans que le systeme de surveillance ait a attendre sa prochaine verification 
		this.cachedDataSurveyService.notifyImmediateChange("Intervenant");
	};

	constructor( //
		@Inject(HttpClient) private httpClient: HttpClient, //
		@Inject(CachedDataSurveyService) private cachedDataSurveyService: CachedDataSurveyService, //
		@Optional() @SkipSelf() previousInstance?: IntervenantService //
	) {
		if (previousInstance) {
			throw new Error(
				'IntervenantService is already loaded. Import it in the AppModule only');
		}
		this.path = (window as any).baseApiPath;
		let intervenantsChanged$ = this.cachedDataSurveyService.getChangeNotification("Intervenant");
		this.intervenantTLRSubService = new ActivityAwareDataSubService<IntervenantTLR>(
			httpClient,
			this.path + 'intervenant?format=TLR',
			(intervenant: IntervenantTLR) => !intervenant.obsolete,
			intervenantsChanged$
		);
	}

	// Fonctions basees sur le cache
	
	public getIntervenantsTLRList = () => this.intervenantTLRSubService.getList();
	// public getIntervenantsTLRMapById = () => this.intervenantTLRSubService.getMapById();
	public getIntervenantTLRById = (id: number) => this.intervenantTLRSubService.getKnownById(id, "IntervenantTLR", );

	// public getActiveIntervenantsTLRMapByEntreprise = () => this.intervenantTLRSubService.getMapActiveByForeignKey("byEntreprise", (intervenant: IntervenantTLR) => intervenant.entrepriseId);
	public getActiveIntervenantsTLRByEntreprise = (id: number) => this.intervenantTLRSubService.getActiveByForeignKey("byEntreprise", (intervenant: IntervenantTLR) => intervenant.entrepriseId, id);
	public getActiveIntervenantsTLR = () => this.intervenantTLRSubService.getActiveList();
	
	// Fonctions CRUD
	
	public create(base: IntervenantTLR): Observable<number> {
		let url = this.path + 'intervenant';
		let result = this.httpClient.post<number>(url, base).pipe(
			tap(this.notifyChanges)
		);
		return result;
	}

	public update(base: IntervenantTLR): Observable<number> {
		let url = this.path + 'intervenant/' + base.id;
		let result = this.httpClient.put<number>(url, base).pipe(
			tap(this.notifyChanges)
		);
		return result;
	}
	
	public delete(intervenantId: number): Observable<any> {
		const url = this.path + 'intervenant/' + intervenantId;
		const request$ = this.httpClient.delete<any>(url, {}).pipe(
			tap(this.notifyChanges)
		);
		return request$;
	}
	
	///////////////////////////////////////
	// Fonctions de lectures specifiques //
	///////////////////////////////////////
	
	// Ancienne fonction, non optimisee par le cache, a remplacer par getIntervenantTLRById ulterieurement
	public getIntervenantById(id: number): Observable<IntervenantEntity> {
		let url = this.path + "intervenant/" + id;
		let result = this.httpClient.get<IntervenantEntity>(url);
		return result;
	}

	// Ancienne fonction, non optimisee par le cache, a remplacer par getIntervenantTLRById ulterieurement
	public getIntervenantsForEntreprise(ids: number[]): Observable<IntervenantEntity[]> {
		let url = this.path + "intervenant/entreprises?idEntrepriseExt=" + ids.join("&idEntrepriseExt=");
		let result = this.httpClient.get<IntervenantEntity[]>(url);
		return result;
	}
	
	// Fonction de recuperation de donnnees agregees avec les PDP
	public getPdpTypesSigned(intervenantId: number, ouvrageId: number): Observable<PdpTypeSigned[]> {
		let url = this.path + "intervenant/" + intervenantId + "/pdp-types-signed?ouvrage=" + ouvrageId;
		let result = this.httpClient.get<PdpTypeSigned[]>(url);
		return result;
	}
	
	// Methode d'obtention des suggestions de noms d'intervenants
	public getSuggestionsNomsIntervenants(criteria: string, quantity: number): Observable<Suggestions<ActivePossibilities, SuggestionNomIntervenant>> {
		let url = this.path + 'intervenant/suggestions-noms?criteria=' + encodeURI(criteria) + '&quantity=' + quantity;
		
		// On ajoute un header special pour empecher l'intercepteur d'afficher un indicateur de chargement
		let headers = new HttpHeaders().set("no-pending-trace", "true");
		
		let result = this.httpClient.get<Suggestions<ActivePossibilities, SuggestionNomIntervenant>>(url, { headers: headers });
		return result;
	}
	
	// Methode d'obtention des suggestions de responsables pour une entreprise
	public getSuggestionsResponsables(entrepriseId: number, criteria: string, quantity: number): Observable<Suggestions<number, IntervenantTLR>> {
		let url = this.path + 'entreprises/' + entrepriseId + '/suggestions-responsables?criteria=' + encodeURI(criteria) + '&quantity=' + quantity;

		// On ajoute un header special pour empecher l'intercepteur d'afficher un indicateur de chargement
		let headers = new HttpHeaders().set("no-pending-trace", "true");
	
		let result = this.httpClient.get<Suggestions<number, IntervenantTLR>>(url, { headers: headers });
		return result;
	}

	// Fonction d'ecriture specifiques
	
	public updateContactInfos(
		idIntervenant: number,
		indicatif: string | null,
		telephone: string | null, 
		email: string | null,
	): Observable<any> {
		let data = {
			idIntervenant: idIntervenant,
			indicatif: this.trimToNull(indicatif),
			phone: this.trimToNull(telephone),
			email: this.trimToNull(email),
		};
		let url = this.path + 'intervenant/updateCoordonnees';
		let result = this.httpClient.put<any>(url, data);
		return result;
	};
	
	private trimToNull(value: string | null | undefined): string | null {
		if (!value) {
			return null;
		}
		let trimmed = value.toString().trim();
		return trimmed.length === 0 ? null : trimmed;		
	}
	
	// Fonction d'export en XLS
	
	public export(filters: IntervenantsFilters): Observable<Blob> {
		let url = this.path + 'intervenant/export';
		
		let httpParams = new HttpParams();
		for (let key in filters) {
			let value = (filters as any)[key];
			if (value !== null && value !== undefined) {
				httpParams = httpParams.set(key, value);				
			}
		}

		return this.httpClient.get(url, { params: httpParams, responseType: 'blob' });
	}
	
	public clearCaches() {
		this.intervenantTLRSubService.clearCaches();
	}
	
}